import { useCallback, useEffect, useState } from 'react';
import { ChatMessage, FeedbackCategory, FeedbackPayload } from './types';
import { submitFeedback } from './api';
import { rememberMyFeedback } from './reflectedNotice';
import { capturePage, currentEnvLabel } from './env';

const REPORTER_KEY = 'dev-feedback-reporter';
const CATEGORY_KEY = 'dev-feedback-category';
/**
 * Draft 정책: **현재 페이지 전용**. 페이지 이동(pathname 변경) 시 자동 clear.
 * → 사용자는 한 페이지의 이슈만 캡처해서 제출. 이동하면 새 페이지에서 새로 시작.
 * localStorage 는 같은 페이지에서 F5 리로드 대비 용도로만 사용 (draft key = pathname 포함).
 */
const DRAFT_PREFIX = 'dev-feedback-draft:';
const draftKey = (path: string) => `${DRAFT_PREFIX}${path}`;

const nowIso = (): string => new Date().toISOString();

export interface UseFeedbackChatOptions {
  appCode: string;
  /** 명시 안하면 window.location.pathname 자동 사용 (변경 감지 → 자동 clear) */
  pageKey?: string;
}

export interface UseFeedbackChat {
  messages: ChatMessage[];
  reporter: string;
  setReporter: (v: string) => void;
  category: FeedbackCategory;
  setCategory: (v: FeedbackCategory) => void;
  addText: (text: string) => void;
  addImage: (file: File) => void;
  addId: (id: string) => void;
  removeAt: (idx: number) => void;
  clear: () => void;
  submitting: boolean;
  /** extraText 를 넘기면 messages 끝에 즉시 text message 로 합쳐 제출 (setState 지연 방어) */
  submit: (extraText?: string) => Promise<number>;
}

const readCurrentPath = (fallback?: string): string => {
  if (fallback) return fallback;
  if (typeof window === 'undefined') return 'unknown';
  return window.location.pathname;
};

export const useFeedbackChat = (opts: UseFeedbackChatOptions): UseFeedbackChat => {
  const [currentPath, setCurrentPath] = useState<string>(() => readCurrentPath(opts.pageKey));

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const raw = localStorage.getItem(draftKey(readCurrentPath(opts.pageKey)));
      if (!raw) return [];
      return JSON.parse(raw) as ChatMessage[];
    } catch {
      return [];
    }
  });

  const [reporter, setReporterState] = useState<string>(() => {
    try {
      return localStorage.getItem(REPORTER_KEY) ?? '';
    } catch {
      return '';
    }
  });

  const [category, setCategoryState] = useState<FeedbackCategory>(() => {
    try {
      const v = localStorage.getItem(CATEGORY_KEY);
      return v === 'operation' || v === 'development' || v === 'confirm' ? v : 'other';
    } catch {
      return 'other';
    }
  });

  const [submitting, setSubmitting] = useState(false);

  const persist = useCallback(
    (next: ChatMessage[]) => {
      try {
        const safe = next.map((m) => ({ ...m, localFile: undefined }));
        localStorage.setItem(draftKey(currentPath), JSON.stringify(safe));
      } catch {
        /* ignore */
      }
    },
    [currentPath],
  );

  useEffect(() => {
    persist(messages);
  }, [messages, persist]);

  // 페이지 이동 감지 — pathname 이 바뀌면 draft 초기화 (localStorage 도 제거)
  useEffect(() => {
    if (typeof window === 'undefined' || opts.pageKey) return;
    let prev = window.location.pathname;
    const onNav = () => {
      const now = window.location.pathname;
      if (now === prev) return;
      try {
        localStorage.removeItem(draftKey(prev));
      } catch {
        /* ignore */
      }
      prev = now;
      setCurrentPath(now);
      setMessages([]);
    };
    // History API monkey-patch (pushState/replaceState 는 이벤트가 없음)
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args: Parameters<typeof origPush>) {
      const r = origPush.apply(this, args);
      onNav();
      return r;
    };
    window.history.replaceState = function (...args: Parameters<typeof origReplace>) {
      const r = origReplace.apply(this, args);
      onNav();
      return r;
    };
    window.addEventListener('popstate', onNav);
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener('popstate', onNav);
    };
  }, [opts.pageKey]);

  const setReporter = useCallback((v: string) => {
    setReporterState(v);
    try {
      localStorage.setItem(REPORTER_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const setCategory = useCallback((v: FeedbackCategory) => {
    setCategoryState(v);
    try {
      localStorage.setItem(CATEGORY_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const addText = useCallback((text: string) => {
    const t = text.trim();
    if (!t) return;
    const ctx = capturePage();
    setMessages((prev) => [
      ...prev,
      { kind: 'text', content: t, ts: nowIso(), pageUrl: ctx.pageUrl, pageTitle: ctx.pageTitle, pageId: ctx.pageId },
    ]);
  }, []);

  const addImage = useCallback((file: File) => {
    const ctx = capturePage();
    setMessages((prev) => [
      ...prev,
      { kind: 'image_ref', content: `pending:${prev.length}`, ts: nowIso(), localFile: file, pageUrl: ctx.pageUrl, pageTitle: ctx.pageTitle, pageId: ctx.pageId },
    ]);
  }, []);

  const addId = useCallback((id: string) => {
    const clean = id.replace(/^#/, '').trim();
    if (!clean) return;
    const ctx = capturePage();
    setMessages((prev) => [
      ...prev,
      { kind: 'id', content: clean, ts: nowIso(), pageUrl: ctx.pageUrl, pageTitle: ctx.pageTitle, pageId: ctx.pageId },
    ]);
  }, []);

  const removeAt = useCallback((idx: number) => {
    setMessages((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    try {
      localStorage.removeItem(draftKey(currentPath));
    } catch {
      /* ignore */
    }
  }, [currentPath]);

  const submit = useCallback(async (extraText?: string): Promise<number> => {
    if (submitting) throw new Error('submitting');
    // 최종 제출 순간 textarea 에 남아있던 leftover 를 immutable 하게 합쳐서 사용
    // (setState 반영 지연으로 messages 가 stale 인 케이스 방어)
    const ctx0 = capturePage();
    const mergedMessages: ChatMessage[] = extraText && extraText.trim()
      ? [
          ...messages,
          {
            kind: 'text',
            content: extraText.trim(),
            ts: nowIso(),
            pageUrl: ctx0.pageUrl,
            pageTitle: ctx0.pageTitle,
            pageId: ctx0.pageId,
          } as ChatMessage,
        ]
      : messages;
    if (mergedMessages.length === 0) throw new Error('메시지가 없습니다');
    // 신고자 미입력 시 'unknown' 폴백 (앱 측에서 로그인 아이디를 setReporter 로 주입하면 그 값 사용)
    const reporterFinal = reporter.trim() || 'unknown';
    setSubmitting(true);
    try {
      const files: File[] = [];
      const chatLog = mergedMessages.map((m) => {
        const base = {
          kind: m.kind,
          content: m.content,
          ts: m.ts,
          pageUrl: m.pageUrl,
          pageTitle: m.pageTitle,
          pageId: m.pageId,
        };
        if (m.kind === 'image_ref' && m.localFile) {
          const idx = files.length;
          files.push(m.localFile);
          return { ...base, content: `attach:${idx}` };
        }
        return base;
      });
      // targetIds: (1) 사용자가 명시적으로 붙인 id pill + (2) submit 시점의 현재 페이지 id 자동 병합.
      // (2) 는 관리자가 "어느 화면에서 남긴 피드백인지" 검색·필터링할 수 있도록 auto-inject.
      // 중복 방지 · pageId 가 비어있으면 skip.
      const explicitIds = mergedMessages.filter((m) => m.kind === 'id').map((m) => m.content);
      const autoPageId = (capturePage().pageId || '').trim();
      const targetIds = autoPageId && !explicitIds.includes(autoPageId)
          ? [...explicitIds, autoPageId]
          : explicitIds;

      // 대표 페이지 = 마지막으로 남긴 message 의 페이지 (사용자가 마지막에 있던 곳)
      // 또는 fallback: submit 시점의 현재 페이지
      const lastWithPage = [...mergedMessages].reverse().find((m) => m.pageUrl);
      const ctx = lastWithPage
        ? {
            pageUrl: lastWithPage.pageUrl!,
            pageTitle: lastWithPage.pageTitle ?? '',
            pageId: lastWithPage.pageId ?? '',
            viewport: '',
            userAgent: '',
          }
        : capturePage();
      const live = capturePage();

      const payload: FeedbackPayload = {
        appCode: opts.appCode,
        env: currentEnvLabel(),
        category,
        pageUrl: ctx.pageUrl || live.pageUrl,
        pageTitle: ctx.pageTitle || live.pageTitle,
        pageId: ctx.pageId || live.pageId,
        targetIds,
        reporter: reporterFinal,
        userAgent: live.userAgent,
        viewport: live.viewport,
        chatLog,
      };

      const { id } = await submitFeedback(payload, files);
      rememberMyFeedback(id);
      clear();
      return id;
    } finally {
      setSubmitting(false);
    }
  }, [submitting, messages, reporter, category, opts.appCode, clear]);

  return {
    messages,
    reporter,
    setReporter,
    category,
    setCategory,
    addText,
    addImage,
    addId,
    removeAt,
    clear,
    submitting,
    submit,
  };
};
